import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { trackZipSet } from "@/lib/analytics";

const AppContext = createContext(null);

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
};

const LS_LOC = "bud_location";
const LS_SAVED = "bud_saved_locations";

export const AppProvider = ({ children }) => {
  // age gate
  const [ageVerified, setAgeVerified] = useState(
    () => localStorage.getItem("bud_age_verified") === "yes"
  );

  // active location
  const [location, setLocation] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_LOC);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  // saved locations (Home, Work, Trip, etc.)
  const [savedLocations, setSavedLocations] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_SAVED);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  // auth
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("bud_token");
    if (!token) {
      setAuthChecked(true);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        localStorage.removeItem("bud_token");
        setUser(null);
      })
      .finally(() => setAuthChecked(true));
  }, []);

  const verifyAge = () => {
    localStorage.setItem("bud_age_verified", "yes");
    setAgeVerified(true);
  };

  const setLocationAndPersist = (loc) => {
    localStorage.setItem(LS_LOC, JSON.stringify(loc));
    setLocation(loc);
    // Operator-funnel: log which ZIPs are setting the search radius.
    if (loc && loc.zip) trackZipSet(loc.zip);
  };

  const clearLocation = () => {
    localStorage.removeItem(LS_LOC);
    setLocation(null);
  };

  const persistSaved = (list) => {
    localStorage.setItem(LS_SAVED, JSON.stringify(list));
    setSavedLocations(list);
  };

  const saveLocation = (loc, label) => {
    if (!loc) return;
    const id =
      loc.id ||
      `${loc.zip || `${loc.lat}_${loc.lon}`}_${(label || loc.city || "spot").toLowerCase()}`;
    const entry = { ...loc, label: label || loc.city || "Saved", id };
    const list = [entry, ...savedLocations.filter((l) => l.id !== id)].slice(0, 6);
    persistSaved(list);
    return entry;
  };

  const removeSavedLocation = (id) => {
    persistSaved(savedLocations.filter((l) => l.id !== id));
  };

  const loginSuccess = ({ token, user }) => {
    localStorage.setItem("bud_token", token);
    setUser(user);
  };

  const logout = () => {
    localStorage.removeItem("bud_token");
    setUser(null);
  };

  return (
    <AppContext.Provider
      value={{
        ageVerified,
        verifyAge,
        location,
        setLocation: setLocationAndPersist,
        clearLocation,
        savedLocations,
        saveLocation,
        removeSavedLocation,
        user,
        authChecked,
        loginSuccess,
        logout,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
